import { h as __h } from "preact";
/** @import { Signal } from "@preact/signals"; */
import { Component, h } from "preact";
/** @import { ComponentChildren, RefObject } from "preact"; */

import { identity } from "~/lib/identity.js";
import html from "~/util/html.js";
/** @import { paginatedResponse, query } from "~/lib/api.js"; */

/** @import { DropdownItem } from "~/component/Dropdown.js"; */
import Button from "~/component/Button.js";
import Data from "~/component/Data.js";

/** @import Pagination from "~/admin/component/Pagination.js"; */
import { StatusChip } from "~/admin/component/StatusChip.js";
/** @import { TableHeader } from "~/admin/component/Table.js"; */
import Table from "~/admin/component/Table.js";
import GroupChip from "../group/GroupChip.js";
import LocationChip from "../location/LocationChip.js";
import MemberChip from "../member/MemberChip.js";
import { connect } from "./actions/connect.js";
import { deleteComputers } from "./actions/deleteComputers.js";
import { renameComputer } from "./actions/renameComputer.js";
import { setAssignee } from "./actions/setAssignee.js";
import { setLocation } from "./actions/setLocation.js";
import util from "~/style/util.js";
import { adopt, importCss, CSS_MODULE_SCRIPTS } from "~/util/css.js";
/** @import { adminMachineResponse } from "~/backend/validate.js"; */

const {
  default: sheet
} = CSS_MODULE_SCRIPTS ? await import("./ComputerTable.module.css", {
  with: {
    type: "css"
  }
}) : await importCss("./ComputerTable.module.css", import.meta.url);
const css = adopt(sheet);

/**
 * @extends {Component<ComputerTableProps>}
 *
 * @typedef {object} ComputerTableProps
 * @property {string} orgId
 * @property {ReturnType<
 * 	typeof query<
 * 		`/ui/admin/orgs/${string}/machines`,
 * 		ReturnType<typeof paginatedResponse<adminMachineResponse>>
 * 	>
 * >} query
 * @property {Signal<Map<string, adminMachineResponse>>} [checked]
 * @property {ComponentChildren} [empty]
 * @property {ComponentChildren} [footer]
 * @property {(h: TableHeader[]) => TableHeader[]} [headers]
 * @property {(
 * 	items: (DropdownItem & { key?: string })[],
 * 	machine: adminMachineResponse,
 * 	anchor: RefObject<HTMLButtonElement>,
 * ) => DropdownItem[]} [onTripleDots]
 * @property {InstanceType<typeof Pagination.State>} [paginate]
 * @property {InstanceType<typeof Table.Sort>} [sort]
 * @property {false} [scroll]
 */
export default class ComputerTable extends Component {
  /** @param {{ local?: boolean }} [options] */
  static sort({
    local = false
  } = {}) {
    return new Table.Sort({
      keys: ["name", "online", "assigned", "location"],
      by: "online",
      order: "desc",
      local
    });
  }
  get #headers() {
    const h = [{
      id: "name",
      label: "Name",
      sort: "name"
    }, {
      id: "status",
      label: "Status",
      sort: "online"
    }, {
      id: "assignedTo",
      label: "Assigned to",
      sort: "assigned"
    }, {
      id: "location",
      label: "Location",
      sort: "location"
    }, {
      id: "connect",
      label: "",
      width: "6rem",
      empty: ""
    }];
    if (this.props.headers) return this.props.headers(h);
    return h;
  }

  /**
   * @returns {(
   * 	machine: adminMachineResponse,
   * 	anchor: { current: HTMLButtonElement },
   * ) => DropdownItem[]}
   */
  get #onTripleDots() {
    const orgId = this.props.orgId;
    const canConnect = identity.permission(orgId, "machine_connect");
    const canEdit = identity.permission(orgId, "machines", "edit");
    return (machine, anchor) => {
      /** @type {(DropdownItem & { key?: string })[]} */
      const items = [];
      if (canConnect) {
        items.push({
          icon: "phaze",
          text: "Connect",
          kind: "primary",
          disabled: !machine.is_online,
          onSelect: () => connect(machine.id)
        });
      }
      if (canEdit) {
        items.push({
          icon: "pencil",
          text: "Rename",
          onSelect: async () => await renameComputer({
            orgId
          }, machine)
        }, {
          icon: "pencil",
          text: "Assign to",
          onSelect: async () => await setAssignee({
            anchor: anchor.current,
            placement: "bottom-end",
            orgId
          }, machine)
        }, {
          icon: "pencil",
          text: "Edit location",
          onSelect: async () => await setLocation({
            anchor: anchor.current,
            placement: "bottom-end",
            orgId
          }, machine)
        }, {
          key: "remove",
          icon: "trash",
          text: "Remove",
          kind: "negative",
          onSelect: async () => {
            const res = await deleteComputers({
              orgId
            }, machine.id);
            if (res) {
              if (this.props.checked) {
                const checked = new Map(this.props.checked.value);
                checked.delete(machine.id);
                this.props.checked.value = checked;
              }
            }
          }
        });
      }
      if (this.props.onTripleDots) return this.props.onTripleDots(items, machine, anchor);
      return items;
    };
  }
  render() {
    const checked = this.props.checked;
    return h(Data, {
      loading: this.props.query.loading && !this.props.query.data,
      error: this.props.query.error,
      children: h(/** @type {typeof Table<adminMachineResponse>} */Table, {
        label: "computer",
        checked: checked?.value,
        onCheck: checked && (check => checked.value = check),
        headers: this.#headers,
        empty: this.props.empty ?? "No computers found.",
        footer: this.props.footer,
        rows: this.props.query.data?.data.map(machine => {
          return {
            key: machine.id,
            label: machine.name,
            value: machine,
            columns: {
              name: ComputerTable.primary(machine),
              status: ComputerTable.status(machine),
              assignedTo: ComputerTable.assigned(machine, this.props.orgId),
              location: ComputerTable.location(machine, this.props.orgId),
              connect: ComputerTable.connect(machine)
            }
          };
        }) ?? [],
        paginate: this.props.paginate?.props(this.props.query),
        sort: this.props.sort?.props(),
        href: machine => `/org/${this.props.orgId}/computers/${machine.id}`,
        onTripleDots: this.#onTripleDots,
        scroll: this.props.scroll
      })
    });
  }

  /** @param {adminMachineResponse} machine */
  static primary(machine) {
    return __h("span", {
      "class": css.primary
    }, machine.name);
  }

  /** @param {adminMachineResponse} machine */
  static connect(machine) {
    if (!machine.is_online || !identity.permission(machine.org_id, "machine_connect")) return null;
    return __h("div", {
      "class": css.connect + " " + util.liftClicks
    }, __h(Button, {
      level: "primary",
      size: "small",
      onClick: () => connect(machine.id)
    }, "Connect"));
  }

  /** @param {adminMachineResponse} machine */
  static status(machine) {
    return __h(StatusChip, {
      online: machine.is_online,
      connections: machine.connections
    });
  }

  /**
   * @param {adminMachineResponse} machine
   * @param {string} org_id
   */
  static assigned(machine, org_id) {
    return [machine.user && __h(MemberChip, {
      member: {
        ...machine.user,
        org_id
      }
    }), machine.group && __h(GroupChip, {
      group: {
        ...machine.group,
        org_id
      }
    })];
  }

  /**
   * @param {adminMachineResponse} machine
   * @param {string} org_id
   */
  static location(machine, org_id) {
    if (!machine.location) return;
    return __h(LocationChip, {
      location: {
        ...machine.location,
        org_id
      }
    });
  }
}